import type {
  Bula,
  Campo,
  Dose,
  Emergencia,
  Escala,
  FontePublicada,
  Formula,
  Laboratorio,
  Mdr1,
  Meta,
  Planta,
  Produto,
  Questao,
  Receita,
  Referencia,
  Regra,
  Sessao,
  Toxico,
  Vacina,
} from "@vetbolso/contracts";
import { agoraEfetivo, baseUtilizavel, colecoesAlteradas, type MetaOffline, relogioVoltou } from "@vetbolso/core";
import Dexie, { type Table } from "dexie";
import { baixarColecao, baixarMapas, ErroApi, lerSessao, SemRede } from "./api.ts";
import type { MapaMental } from "./mapas-dados.ts";

/** Conteúdo da release como o app usa. Coleções ausentes viram listas vazias. */
export type Base = {
  bulario: Bula[];
  doses: Dose[];
  toxicos: Toxico[];
  produtos: Produto[];
  plantas: Planta[];
  vacinas: Vacina[];
  regras: Regra[];
  formulas: Formula[];
  referencia: Referencia[];
  laboratorio: Laboratorio[];
  questoes: Questao[];
  escalas: Escala[];
  fontes: FontePublicada[];
  emergencia?: Emergencia;
  mdr1?: Mdr1;
  receita?: Receita;
  campo?: Campo;
  meta?: Meta;
  auditoria?: string[];
  lacunas?: string[];
};
const LISTAS = [
  "bulario",
  "doses",
  "toxicos",
  "produtos",
  "plantas",
  "vacinas",
  "regras",
  "formulas",
  "referencia",
  "laboratorio",
  "questoes",
  "escalas",
  "fontes",
] as const;

class BancoVet extends Dexie {
  colecoes!: Table<{ nome: string; dados: unknown }, string>;
  meta!: Table<{ k: "meta"; v: MetaOffline }, string>;
  mapas!: Table<{ k: "mapas"; versao: string; dados: MapaMental[] }, string>;
  constructor() {
    super("vetbolso");
    this.version(1).stores({ colecoes: "nome", meta: "k" });
    this.version(2).stores({ mapas: "k" });
  }
}
export const banco = new BancoVet();

export type EstadoBase =
  | { fase: "carregando" }
  | { fase: "entrar" }
  | { fase: "sem_acesso"; sessao: Sessao }
  | { fase: "sem_release"; sessao: Sessao }
  | { fase: "expirada"; motivo: "janela" | "relogio" }
  | { fase: "erro"; msg: string }
  | { fase: "pronta"; base: Base; meta: MetaOffline; sessao: Sessao | null; online: boolean };

const lerMeta = async () => (await banco.meta.get("meta"))?.v ?? null;

export async function apagarBase() {
  await banco.transaction("rw", banco.colecoes, banco.meta, banco.mapas, async () => {
    // Meta sai primeiro: sem ela, coleções órfãs nunca são lidas.
    await banco.meta.clear();
    await banco.colecoes.clear();
    await banco.mapas.clear();
  });
}

async function lerBase(): Promise<Base> {
  const linhas = await banco.colecoes.toArray();
  const b = Object.fromEntries(linhas.map((l) => [l.nome, l.dados])) as Partial<Base>;
  for (const k of LISTAS) if (!Array.isArray(b[k])) (b as Record<string, unknown>)[k] = [];
  return b as Base;
}

/** Sem servidor: vale a base salva, dentro da janela de 7 dias e com o relógio andando para a frente. */
async function offline(meta: MetaOffline | null): Promise<EstadoBase> {
  if (!meta) return { fase: "erro", msg: "Sem conexão. Conecte-se uma vez para baixar a base de consulta." };
  const agora = Date.now();
  if (relogioVoltou(meta, agora)) return { fase: "expirada", motivo: "relogio" };
  if (!baseUtilizavel(meta, agora)) return { fase: "expirada", motivo: "janela" };
  const v = { ...meta, vistoEm: agoraEfetivo(meta, agora) };
  await banco.meta.put({ k: "meta", v });
  return { fase: "pronta", base: await lerBase(), meta: v, sessao: null, online: false };
}

/** Mapas do módulo pago: reembolso apaga do aparelho; falha ao baixar não trava a base e tenta de novo na próxima sincronização. */
async function sincronizarMapas(sessao: Sessao) {
  if (!sessao.acesso.mapas) return banco.mapas.clear();
  try {
    const novos = await baixarMapas((await banco.mapas.get("mapas"))?.versao);
    if (novos) await banco.mapas.put({ k: "mapas", ...novos });
  } catch (e) {
    if (!(e instanceof SemRede || e instanceof ErroApi)) throw e;
  }
}

/**
 * Confirma o acesso e baixa só as coleções que mudaram. As coleções são gravadas
 * antes da meta, na mesma transação: uma sincronização interrompida nunca deixa
 * meta nova apontando para coleção velha.
 */
export async function sincronizar(): Promise<EstadoBase> {
  const meta = await lerMeta();
  let sessao: Sessao;
  try {
    sessao = await lerSessao();
  } catch (e) {
    if (e instanceof SemRede) return offline(meta);
    if (e instanceof ErroApi && e.status === 401) return { fase: "entrar" };
    throw e;
  }
  if (meta && meta.contaId !== sessao.usuario.id) await apagarBase();
  // Reembolso ou revogação derrubam o acesso também no aparelho.
  if (!sessao.acesso.ativo) {
    await apagarBase();
    return { fase: "sem_acesso", sessao };
  }

  for (let tentativa = 0; tentativa < 3; tentativa++) {
    const release = sessao.release;
    if (!release) return { fase: "sem_release", sessao };
    const atual = meta && meta.contaId === sessao.usuario.id ? meta : null;
    const faltam = colecoesAlteradas(atual?.colecoes, release.colecoes);
    try {
      const baixadas = await Promise.all(faltam.map(async (nome) => ({ nome, dados: await baixarColecao(nome, release.versao) })));
      const agora = Date.now();
      const nova: MetaOffline = {
        schemaVersion: 1,
        versao: release.versao,
        contaId: sessao.usuario.id,
        expiraEm: sessao.acesso.expiraEm,
        salvoEm: faltam.length ? agora : (atual?.salvoEm ?? agora),
        confirmadoEm: agora,
        vistoEm: Math.max(agora, atual?.vistoEm ?? 0),
        colecoes: release.colecoes,
      };
      await banco.transaction("rw", banco.colecoes, banco.meta, async () => {
        if (baixadas.length) await banco.colecoes.bulkPut(baixadas);
        const sobram = (await banco.colecoes.toCollection().primaryKeys()).filter((n) => !(n in release.colecoes));
        if (sobram.length) await banco.colecoes.bulkDelete(sobram);
        await banco.meta.put({ k: "meta", v: nova });
      });
      await sincronizarMapas(sessao);
      return { fase: "pronta", base: await lerBase(), meta: nova, sessao, online: true };
    } catch (e) {
      if (e instanceof ErroApi && e.status === 409) {
        sessao = await lerSessao();
        continue;
      }
      if (e instanceof ErroApi && e.status === 403) {
        await apagarBase();
        return { fase: "sem_acesso", sessao };
      }
      if (e instanceof SemRede) return offline(meta);
      throw e;
    }
  }
  return { fase: "erro", msg: "A base mudou várias vezes durante o download. Tente de novo em instantes." };
}
